/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Fragment } from 'preact';
import { forwardRef, ForwardedRef } from 'preact/compat';
import { useRef, useCallback, useEffect, useState, useImperativeHandle } from 'preact/hooks';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { containsKey, Keys, isKeyDefined } from '../utils/UNSAFE_keys';
import {
  getPrevNextKeyByCount,
  getFirstVisibleKey,
  getViewportConfig,
  findElementByKey,
  keyExtractor,
  handleSelectionRange,
  ITEM_SELECTOR
} from '../utils/PRIVATE_collectionUtils';
import { useCurrentKey } from '../hooks/PRIVATE_useCurrentKey';
import { useCollectionFocusRing } from '../hooks/PRIVATE_useCollectionFocusRing';
import { useTabbableModeSet } from '../hooks/PRIVATE_useTabbableModeSet';
import { useSelection, SelectionRangeDetail } from '../hooks/PRIVATE_useSelection';
import { ReorderableContext } from '../hooks/PRIVATE_useReorderable';
import { ItemContext, ListItemRendererContext, SelectionDetail } from '../UNSAFE_Collection';
import { Selector } from '../UNSAFE_Selector';
import { HiddenAccessible } from '../UNSAFE_HiddenAccessible';
import { LoadMoreCollection } from '../PRIVATE_LoadMoreCollection';
import { styles } from './themes/BaseCardViewStyles.css';
import { Props } from '../UNSAFE_CardFlexView';
import { MemoizeBaseCardViewItem } from './BaseCardViewItem';
import { BaseCardViewLayout } from './BaseCardViewLayout';
import {
  DefaultLoadMoreSkeletons,
  LoadMoreSkeletons,
  InitialLoadSkeletons
} from './BaseCardViewSkeleton';
import { gutterSizeToPX, emptyKeys } from './BaseCardViewUtils';
import { useCardViewReorder } from './useCardViewReorder';
// TODO: add this import due to issue JET-67485, need to update once Stable is introduced
import './themes/redwood/BaseCardViewBaseTheme.css';

/**
 * Props specific for the GradGridBaseImpl Component
 */
type BaseCardViewImplProps<K, D> = Props<K, D> & {
  /**
   * Provide the number of columns to render;
   */
  columns: number;
  /**
   * The layout of the BaseCardView. When we use CardGridView as parent, we will use grid layout;
   * when we use CardFlexView as parent, we will use flex layout.
   */
  layout: 'grid' | 'flex';
  /**
   * Card dimension, including width and height
   */
  cardSize: { width?: number; height?: number };
  /**
   * A callback function to update card dimension, including width and height
   */
  updateCardSize: (width: number, height: number) => void;
};

const BaseCardViewImpl = forwardRef(
  <K extends string | number, D>(
    {
      children,
      data,
      onLoadMore = () => {},
      hasMore = false,
      getRowKey,
      currentKey,
      onCurrentKeyChange,
      selectionMode = 'none',
      selectedKeys = emptyKeys as Keys<K>,
      onSelectionChange,
      onReorder,
      viewportConfig,
      focusBehavior = 'card',
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      initialAnimation = 'slideUp',
      gutterSize = 'sm',
      columns,
      layout,
      cardSize,
      updateCardSize
    }: BaseCardViewImplProps<K, D>,
    ref?: ForwardedRef<HTMLDivElement>
  ) => {
    const rootRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref!, () => rootRef.current!, [rootRef]);

    // tracking the anchor key which is used for shift+click selection
    const anchorKey = useRef<K | undefined>();
    // tracking pending current key in pointer down event
    const pendingCurrentKey = useRef<K>();

    const gutterSizePX = gutterSizeToPX[gutterSize];
    const layoutProps = { layout, columns, gutterSize };
    const isInitialAnimation = useRef(true);
    useEffect(() => {
      if (cardSize.height != undefined && cardSize.width != undefined) {
        isInitialAnimation.current = false;
      }
    }, [cardSize]);

    const ariaMultiSelectable = selectionMode === 'none' ? undefined : selectionMode === 'multiple';
    const ariaRowcount = hasMore || !data || columns < 1 ? -1 : Math.ceil(data?.length / columns);
    const ariaColcount = columns < 1 ? -1 : columns;

    const [isFocusBehaviorValid, setIsFocusBehaviorValid] = useState(true);
    const validFocusBehavior = isFocusBehaviorValid ? focusBehavior : 'card';

    // ensure current item is scrolled into view when current item is updated
    // during reordering, enable that during keyboard reordering, disable that during mouse reordering
    const scrollCurrItemIntoView = useRef(false);
    const isReorderEnabled = onReorder != undefined;

    const isFetching = useRef(false);

    /*****************************************************************************************/
    /************************************* Tabbable Mode *************************************/
    /*****************************************************************************************/

    const [isCurrentTabbableKey, tabbableModeProps] = useTabbableModeSet(
      rootRef,
      (element) => {
        return keyExtractor(element, ITEM_SELECTOR) as K;
      },
      currentKey,
      onCurrentKeyChange
    );

    /*****************************************************************************************/
    /********************************* Current Key and Focus *********************************/
    /*****************************************************************************************/

    const { currentKeyProps } = useCurrentKey(
      (element) => keyExtractor(element, ITEM_SELECTOR),
      selectionMode !== 'multiple',
      // init a function every time to make sure we pass in the latest columns,
      // otherwise the new key might be wrong after resize
      () => getPrevNextKeyByCount(data, getRowKey, currentKey, -columns),
      () => getPrevNextKeyByCount(data, getRowKey, currentKey, columns),
      () => getPrevNextKeyByCount(data, getRowKey, currentKey, -1),
      () => getPrevNextKeyByCount(data, getRowKey, currentKey, 1),
      currentKey,
      onCurrentKeyChange
    );

    const [showFocusRing, focusRingProps] = useCollectionFocusRing(rootRef, [
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight'
    ]);

    const handleFocus = () => {
      // handle initial focus
      if (
        !isKeyDefined(currentKey) &&
        onCurrentKeyChange &&
        isCurrentTabbableKey(undefined) &&
        rootRef.current
      ) {
        const firstKey =
          pendingCurrentKey.current || (getFirstVisibleKey(rootRef.current, ITEM_SELECTOR) as K);
        if (isKeyDefined(firstKey)) {
          onCurrentKeyChange({ value: firstKey });
        }
      } else if (isKeyDefined(currentKey) && rootRef.current) {
        // if currentKey is defined, make sure scroll the current item into view when cardview regains focus
        const elem = findElementByKey(rootRef.current, currentKey as K, ITEM_SELECTOR);
        elem?.scrollIntoView({ block: 'nearest' });
      }
    };

    // need to track pointer down element to set currentItem when initial focus happens
    const handlePointerDown = (event: PointerEvent) => {
      const key = keyExtractor(event.target as HTMLElement, ITEM_SELECTOR);
      if (isKeyDefined(key)) {
        pendingCurrentKey.current = key as K;
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      // for content focusBehavior, when users tab out, we need to focus back to card grid, to ensure
      // the tabbing order works properly, otherwise the focus might get stuck at the current item
      if (validFocusBehavior === 'content' && event.key === 'Tab') {
        rootRef.current?.focus({ preventScroll: true });
      }
      // during keyboard reordering, the current item should be scrolled into view
      const keyHandlers = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
      if (
        isReorderEnabled &&
        (event.metaKey || event.ctrlKey) &&
        event.shiftKey &&
        keyHandlers.includes(event.key)
      ) {
        scrollCurrItemIntoView.current = true;
      }
    };

    useEffect(() => {
      if (currentKey != null && rootRef.current) {
        const elem = findElementByKey(rootRef.current, currentKey, ITEM_SELECTOR);
        if (elem) {
          // only update aria-activedescendant for screen reader in default focusBehavior
          if (validFocusBehavior === 'card') {
            const cell = elem.closest(`[role=gridcell]`);
            const activeDescendant = rootRef.current.getAttribute('aria-activedescendant');
            if (cell && activeDescendant !== cell.id) {
              rootRef.current.setAttribute('aria-activedescendant', cell.id);
              scrollCurrItemIntoView.current = true;
            }
          }
          // make sure current item is visible
          if (scrollCurrItemIntoView.current) {
            elem.scrollIntoView({ block: 'nearest' });
            scrollCurrItemIntoView.current = false;
          }
        }
      }
      // listen to data because the current item would re-render when data is updated, i.e. the id
      // of current item would be updated, so the currentKey itself couldn't guarantee the root has
      // the latest aria-activedescendant
    }, [currentKey, data, validFocusBehavior, isReorderEnabled]);

    /*****************************************************************************************/
    /*************************************** Selection ***************************************/
    /*****************************************************************************************/

    const handleSelectionChange = useCallback(
      (detail: SelectionDetail<K>) => {
        if (onSelectionChange) {
          if (detail.value.all === false && detail.value.keys.size > 0) {
            // during regular selection, the last key selected by a user gesture is the anchor key
            anchorKey.current = Array.from(detail.value.keys.values()).pop();
          }
          onSelectionChange(detail);
        }
      },
      [anchorKey, onSelectionChange]
    );

    const handleSelectionRangeChange = useCallback(
      (detail: SelectionRangeDetail<K>) => {
        if (data && onSelectionChange) {
          const value = handleSelectionRange(detail, data, getRowKey);
          // for non-virtualized CardView, value should always be an array
          if (Array.isArray(value)) {
            if (detail.eventType === 'shiftSpace') {
              // during range selection by shift+space, the first key selected by a user gesture is the anchor key
              anchorKey.current = detail.value.start;
            } else {
              // during range selection by shift+click/arrow keys, the last key selected by a user gesture is the anchor key
              anchorKey.current = detail.value.end;
            }
            onSelectionChange({ value: { all: false, keys: new Set(value) }, target: null });
          }
        }
      },
      [data, onSelectionChange, getRowKey]
    );

    const { selectionProps } = useSelection(
      (element: HTMLElement) => {
        if (element === rootRef.current) {
          return currentKey === undefined ? null : currentKey;
        }
        return keyExtractor(element, ITEM_SELECTOR) as K;
      },
      selectedKeys,
      selectionMode,
      false,
      'toggle',
      false,
      handleSelectionChange,
      anchorKey.current,
      currentKey,
      (currentKey: K | undefined, isPrev: boolean) => () =>
        getPrevNextKeyByCount(data, getRowKey, currentKey, isPrev ? -columns : columns),
      (currentKey: K | undefined, isPrev: boolean) => () =>
        getPrevNextKeyByCount(data, getRowKey, currentKey, isPrev ? -1 : 1),
      (key: K) => {
        if (rootRef.current) {
          const elem = findElementByKey(rootRef.current, key, ITEM_SELECTOR);
          if (elem) {
            elem.scrollIntoView({ block: 'nearest' });
          }
        }
      },
      handleSelectionRangeChange
    );

    /*****************************************************************************************/
    /**************************************** Reorder ****************************************/
    /*****************************************************************************************/

    const {
      reorderProps,
      reorderItemProps,
      reorderContext,
      reorderInstructionsId,
      reorderInstructions
    } = useCardViewReorder({
      currentKey,
      onReorder,
      gutterSize,
      columns,
      rootRef,
      scrollCurrItemIntoView
    });

    const reorderAcc = isReorderEnabled && (
      <>
        <span id={reorderInstructionsId}>
          <HiddenAccessible>{reorderInstructions}</HiddenAccessible>
        </span>
      </>
    );

    /*****************************************************************************************/
    /************************************* Load More *****************************************/
    /*****************************************************************************************/

    const handleLoadMore = useCallback(() => {
      // check isFetching to avoid multiple load more at the same time (details in JET-61117)
      if (!isFetching.current) {
        isFetching.current = true;
        onLoadMore();
      }
    }, [isFetching, onLoadMore]);

    useEffect(() => {
      isFetching.current = false;
      // listen to data and hasMore because when one of them gets updated, the fetching is done
    }, [data, hasMore]);

    /*****************************************************************************************/
    /************************************ Child Component ************************************/
    /*****************************************************************************************/

    const getItemContext = useCallback(
      (context: ItemContext<D>) => {
        const key = getRowKey(context.data);
        const selectorRenderer =
          selectionMode === 'multiple'
            ? () => (
                <Selector
                  onChange={onSelectionChange}
                  rowKey={key}
                  selectedKeys={selectedKeys}></Selector>
              )
            : undefined;

        return {
          index: context.index,
          data: context.data,
          metadata: { key: key },
          selector: selectorRenderer,
          isSelected: containsKey(selectedKeys, key)
        };
      },
      [selectionMode, selectedKeys, onSelectionChange, getRowKey]
    );

    const childrenComponent = (context: ItemContext<D>) => {
      const cardItemContext = getItemContext(context);
      const isTabbable = isCurrentTabbableKey(cardItemContext.metadata.key);
      const isFocused = currentKey === cardItemContext.metadata.key && !isTabbable;
      const isFocusRingVisible = isFocused && showFocusRing;
      const isSelected = cardItemContext.isSelected;
      const itemRenderFunc = (context: ListItemRendererContext<K, D>) => (
        <MemoizeBaseCardViewItem
          key={cardItemContext.metadata.key}
          context={context}
          isTabbable={isTabbable}
          isFocused={isFocused}
          isFocusRingVisible={isFocusRingVisible}
          isGridLayout={layout === 'grid'}
          isSelected={isSelected}
          selectionMode={selectionMode}
          focusBehavior={validFocusBehavior}
          {...(focusBehavior === 'content' && { setIsFocusBehaviorValid })}
          {...(isInitialAnimation.current && { initialAnimation })}
          {...(cardItemContext.index === 0 && !cardSize.width && { updateCardSize })}
          {...(isReorderEnabled && { reorderItemProps })}>
          {children}
        </MemoizeBaseCardViewItem>
      );
      return itemRenderFunc(cardItemContext);
    };

    // todo: issue track in JET-57952, need a way to show the skeletons in keyboard navigation
    const skeletons =
      data && !data.length && hasMore ? (
        <DefaultLoadMoreSkeletons layout={layout} />
      ) : (
        <LoadMoreSkeletons
          layout={layout}
          colCount={columns}
          totalCount={data?.length || 0}
          cardWidth={cardSize.width}
          cardHeight={cardSize.height}
        />
      );

    viewportConfig = getViewportConfig(rootRef, viewportConfig);
    const collectionComponent = (
      <div role="row">
        <BaseCardViewLayout {...layoutProps}>
          <LoadMoreCollection
            data={data}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            loadMoreIndicator={skeletons}
            loadMoreThreshold={gutterSizePX + 4} // gap size + padding
            viewportConfig={viewportConfig}>
            {childrenComponent}
          </LoadMoreCollection>
        </BaseCardViewLayout>
      </div>
    );

    const initialLoadSkeletons = (
      <BaseCardViewLayout {...layoutProps}>
        <InitialLoadSkeletons layout={layout} />
      </BaseCardViewLayout>
    );

    return (
      <div
        {...mergeProps(
          currentKeyProps,
          focusRingProps,
          selectionProps,
          reorderProps,
          {
            onFocus: handleFocus,
            onPointerDown: handlePointerDown,
            onKeyDown: handleKeyDown
          },
          // only enable tabbable mode in default focusBehavior
          { ...(validFocusBehavior === 'card' && tabbableModeProps) }
        )}
        ref={rootRef}
        role="grid"
        class={styles.baseStyle}
        tabIndex={0}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-multiselectable={ariaMultiSelectable}
        aria-rowcount={ariaRowcount}
        aria-colcount={ariaColcount}>
        <ReorderableContext.Provider value={reorderContext}>
          {!data ? (
            initialLoadSkeletons
          ) : data.length || hasMore ? (
            collectionComponent
          ) : (
            <Fragment />
          )}
        </ReorderableContext.Provider>
        {reorderAcc}
      </div>
    );
  }
);

/**
 * Props specific for the BaseCardView
 */
type BaseCardViewProps<K, D> = BaseCardViewImplProps<K, D> & {
  ref?: ForwardedRef<HTMLDivElement>;
};

/**
 * Cast BaseCardViewImpl to generic type, because BaseCardViewImpl would infer the generic type to specific type,
 * i.e infer <K extends string | number, D> to <string | number, unknown>, which will cause type issues in the parent component
 */
export const BaseCardView = BaseCardViewImpl as <K extends string | number, D>(
  props: BaseCardViewProps<K, D>
) => JSX.Element;
